import { MenuItem, useSnackbar } from '@chronosphereio/chrono-ui';
import { BlobOptions, SnapdomOptions, snapdom } from '@zumer/snapdom';
import { useCallback } from 'react';
import { usePanelKey } from '../VizPanelContextProvider';
import { getFormattedPanelKey } from './utils';

type PanelExportVariant = 'download' | 'copy';

export function VizPanelExportMenuItem({
  panelKey,
  panelTitle,
  variant = 'download',
}: {
  panelKey?: string;
  panelTitle?: string;
  variant?: PanelExportVariant;
}) {
  const contextPanelKey = usePanelKey();
  return (
    <DownloadVizPanelImageMenuItem variant={variant} panelKey={panelKey ?? contextPanelKey} panelTitle={panelTitle} />
  );
}

function DownloadVizPanelImageMenuItem({
  panelKey,
  panelTitle,
  variant,
}: {
  panelKey: string;
  panelTitle?: string;
  variant: PanelExportVariant;
}) {
  const { infoSnackbar, errorSnackbar } = useSnackbar();

  const handleDownload = useCallback(async () => {
    // This unique identifier must match the id set on the Container component in VizPanel.tsx
    const panelContainerId = getFormattedPanelKey(panelKey)!;
    const node = document.getElementById(panelContainerId);
    if (!node) {
      errorSnackbar('Panel container not found for image capture');
      return;
    }

    const options = {
      embedFonts: true,
      type: 'png',
    } satisfies SnapdomOptions & BlobOptions;

    if (variant === 'copy') {
      try {
        const blob = await snapdom.toBlob(node, options);
        if (!blob) {
          errorSnackbar('Failed to create image blob');
          return;
        }
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        infoSnackbar('Panel image copied to clipboard');
      } catch {
        errorSnackbar('Failed to copy panel image to clipboard');
      }
    } else if (variant === 'download') {
      try {
        const blob = await snapdom.toBlob(node, options);
        if (!blob) {
          errorSnackbar('Failed to create image blob');
          return;
        }
        const dataUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.download = `${panelTitle ?? panelContainerId}.png`;
        link.href = dataUrl;
        link.click();

        URL.revokeObjectURL(dataUrl);
        link.remove();
      } catch {
        errorSnackbar('Failed to export panel image');
      }
    }
  }, [variant, panelKey, panelTitle, infoSnackbar, errorSnackbar]);

  return <MenuItem onClick={handleDownload}>{variant === 'download' ? 'Export as PNG' : 'Copy as image'}</MenuItem>;
}
